import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../../Services/notification.service';
import { CategoryService } from '../../../../Services/Category/category.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'app-add-category',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './add-category.component.html',
  styleUrls: ['./add-category.component.css'],
})
export class AddCategoryComponent implements OnInit {
  addCategoryForm!: FormGroup;
  selectedFirmId!: number;
  categories: any;
  isEditMode: boolean = false;
  editingCategoryId: number | null = null;

  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  pagedCategories: any[] = [];
  selectedCategoryFilter: string = '';



  constructor(
    private fb: FormBuilder,
    private categoryService: CategoryService,
    private notificationService: NotificationService,
    private firmSelectionService: FirmSelectionService
  ) { }

  ngOnInit(): void {
    this.initCategoryForm();

    // Get selected firm
    this.firmSelectionService.selectedFirmName$.subscribe((firm: any) => {
      if (firm?.id) {
        this.selectedFirmId = firm.id;
      }
    });

    this.addCategoryForm.get('newType')?.valueChanges.subscribe((value) => {
      const unitControl = this.addCategoryForm.get('newUnit');
      const subTypeControl = this.addCategoryForm.get('newSubType');

      if (value === 'Product') {
        unitControl?.enable();
        unitControl?.setValue('By MRP');
        unitControl?.disable();
      } else {
        if (unitControl?.disabled) {
          unitControl?.enable();
        }
      }

      if (value === 'Master') {
        subTypeControl?.setValidators([Validators.required]);
      } else {
        subTypeControl?.clearValidators();
        subTypeControl?.setValue('');
      }
      subTypeControl?.updateValueAndValidity();

      if (value === 'Stone' || value === 'Metal') {
        unitControl?.clearValidators();
        unitControl?.setValue('');
      } else {
        unitControl?.setValidators([Validators.required]);
      }
      unitControl?.updateValueAndValidity();
    });

    this.getCategories();
  }

  initCategoryForm() {
    this.addCategoryForm = this.fb.group({
      newType: [''],
      newSubType: [''],
      newCategoryName: ['', Validators.required],
      newCode: ['', Validators.required],
      newUnit: ['', Validators.required],
      MRP: [''],
    });
  }

  onCategoryFilterChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedCategoryFilter = value;
    this.getCategories(value);
  }

  getCategories(type: string = '') {
    let params = new HttpParams();
    if (type) {
      params = params.set('type', type);
    }

    this.categoryService.getCategories(params).subscribe((response: any) => {

      this.categories = response.data
        .filter((item: any) => item.Type !== null && item.Type !== '');

      this.currentPage = 1; // reset page on filter
      this.setupPagination();
    });
  }

  setupPagination(): void {
    this.totalPages = Math.ceil(this.categories.length / this.pageSize);
    this.updatePagedCategories();
  }

  updatePagedCategories(): void {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    this.pagedCategories = this.categories.slice(startIndex, endIndex);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePagedCategories();
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagedCategories();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagedCategories();
    }
  }

  addCategory(): void {

    if (!this.selectedFirmId) {
      this.notificationService.showError(
        'Firm not selected. Cannot proceed.',
        'Category'
      );
      return;
    }

    const formData = this.addCategoryForm.getRawValue();

    if (formData.newType === 'Master' && !formData.newSubType) {
      this.addCategoryForm.get('newSubType')?.markAsTouched();
      this.notificationService.showError('Please select Lot / Piece.', 'Validation Error');
      return;
    }

    if (this.addCategoryForm.invalid) {
      this.addCategoryForm.markAllAsTouched();
      this.notificationService.showError('Please fill in all required fields.', 'Validation Error');
      return;
    }

    const payload = {
      Type: formData.newType,
      SubType: formData.newSubType,
      name: formData.newCategoryName,
      code: formData.newCode,
      unit: formData.newUnit,
      mrp: formData.MRP,
      firm_id: this.selectedFirmId,
    };


    // ✅ UPDATE MODE
    if (this.isEditMode && this.editingCategoryId) {

      this.categoryService.updateCategory(this.editingCategoryId, payload)
        .subscribe({
          next: () => {
            this.notificationService.showSuccess(
              'Category updated successfully!',
              'Update Category'
            );
            this.resetForm(true);
            this.getCategories(this.selectedCategoryFilter);
          },
          error: (err) => {
            console.error(err);
            this.notificationService.showError(
              'Failed to update category.',
              'Update Category'
            );
          }
        });

    } else {
      // ✅ CREATE MODE
      this.categoryService.createCategory(payload)
        .subscribe({
          next: () => {
            this.notificationService.showSuccess(
              `Category "${payload.name}" added successfully.`,
              'Add Category'
            );
            this.resetForm(true);
            this.getCategories(this.selectedCategoryFilter);

          },
          error: (err) => {
            console.error(err);
            this.notificationService.showError(
              'Failed to save category.',
              'Add Category'
            );
          }
        });
    }
  }

  resetForm(preserveType: boolean = false) {
    const previousType = preserveType ? this.addCategoryForm.get('newType')?.value : '';
    const previousSubType = preserveType ? this.addCategoryForm.get('newSubType')?.value : '';

    this.isEditMode = false;
    this.editingCategoryId = null;

    this.addCategoryForm.get('newUnit')?.enable();

    this.addCategoryForm.reset({
      newType: previousType || '',
      newSubType: previousSubType || '',
      newCategoryName: '',
      newCode: '',
      newUnit: '',
      MRP: ''
    });

    if (previousType) {
      this.addCategoryForm.patchValue({
        newType: previousType,
        newSubType: previousSubType || ''
      });
    }
  }

  editCategory(category: any) {
    this.isEditMode = true;
    this.editingCategoryId = category.id;

    this.addCategoryForm.patchValue({
      newType: category.Type ?? '',
      newSubType: category.SubType ?? '',
      newCategoryName: category.name,
      newCode: category.code,
      newUnit: category.unit,
      MRP: category.mrp
    });

    // Optional UX: scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  deleteCategory(id: number) {
    const isDeleted = confirm('Do you really want to delete this category?');

    if (isDeleted) {
      this.categoryService.deleteCategory(id).subscribe({
        next: () => {
          this.notificationService.showSuccess(
            'Category deleted successfully!',
            'Success'
          );
          this.getCategories();
        },
        error: () => {
          this.notificationService.showError(
            'Something went wrong while deleting the category',
            'Error'
          );
        }
      });
    } else {
      this.notificationService.showWarning(
        'Category not deleted',
        'Warning'
      );
    }
  }
  allowOnlyNumbers(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab'
    ];

    if (
      allowedKeys.includes(event.key) ||
      /^[0-9]$/.test(event.key)
    ) {
      return;
    }

    event.preventDefault();
  }

}
